// src/components/ProtectedRoute.js
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useProfile } from '../../context/ProfileContext';

/**
 * Enhanced Route Guard Component
 * Handles registered onboarding checks while safely managing guest session visibility.
 */
const ProtectedRoute = ({ children }) => {
  const { user, userProfile, loading } = useProfile();
  const location = useLocation();

  // 1. Maintain hold screen while context parameters resolve
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950 text-white">
        <p className="text-sm font-bold uppercase tracking-widest animate-pulse">
          Verifying Session State...
        </p>
      </div>
    );
  }

  // 2. Absolute baseline block: If no active session context exists, route to login
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 3. 👻 THE GUEST ROUTE GATING EXCEPTION:
  if (user.isAnonymous) {
    const standardPrivateZones = ['/settings', '/upload-music', '/dashboard', '/add-payment-method'];
    const matchesStandardZone = standardPrivateZones.some(path => location.pathname.startsWith(path));
    const isBaseProfileZone = location.pathname === '/profile' || location.pathname === '/profile/';

    if (matchesStandardZone || isBaseProfileZone) {
      console.warn(`GATEKEEPER: Guest blocked from private zone: ${location.pathname}. Redirecting to login.`);
      return <Navigate to="/login" state={{ from: location }} replace />;
    }

    if (location.pathname === '/onboarding') {
      return <Navigate to="/home" replace />;
    }

    return children;
  }

  // 4. 🛡️ REGISTERED USER ONBOARDING GATE (UPDATED STEP-AWARE LOGIC):
  // A user is only considered fully cleared if their document states they finished step 7.
  const isTrulyOnboarded = userProfile && userProfile.isOnboarded === true && (userProfile.onboardingStep >= 7);

  // If they haven't finished all 7 steps, catch them and redirect them to the wizard flow
  if (userProfile && !isTrulyOnboarded && location.pathname !== '/onboarding') {
    console.warn(`GATEKEEPER: Onboarding incomplete (Step ${userProfile.onboardingStep || 1}/7). Redirecting to wizard.`);
    return <Navigate to="/onboarding" replace />;
  }

  // Prevent fully activated user accounts from back-tracking into onboarding steps
  if (isTrulyOnboarded && location.pathname === '/onboarding') {
    return <Navigate to="/home" replace />;
  }

  return children;
};

export default ProtectedRoute;